/**
 * PageHeader - Encabezado de página
 * Con título, breadcrumbs y botones de acción
 */
import PropTypes from 'prop-types';
import { Box, Typography, Breadcrumbs, Link, Button } from '@mui/material';
import { NavigateNext, Home } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

/**
 * @component PageHeader
 * @description Encabezado de página con título, breadcrumbs y acciones
 * @param {Object} props
 * @param {string} props.title - Título de la página
 * @param {string} [props.subtitle] - Subtítulo de la página
 * @param {Array} [props.breadcrumbs] - Breadcrumbs de navegación
 *   Ejemplo: [{ label: 'Inicio', to: '/' }, { label: 'Cheques' }]
 * @param {Array|ReactNode} [props.actions] - Botones de acción (array de objetos o JSX)
 *   Ejemplo: [{ label: 'Nuevo', onClick: fn, icon: AddIcon, variant: 'contained' }]
 */
const PageHeader = ({ title, subtitle, breadcrumbs = [], actions = [] }) => {
  return (
    <Box sx={{ mb: 3 }}>
      {/* Breadcrumbs */}
      {breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" />}
          sx={{ mb: 1 }}
        >
          {/* Siempre incluir home */}
          <Link
            component={RouterLink}
            to="/dashboard"
            underline="hover"
            color="inherit"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: '0.875rem',
            }}
          >
            <Home fontSize="small" />
            Inicio
          </Link>

          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;

            return isLast || !crumb.to ? (
              <Typography
                key={index}
                color="text.primary"
                sx={{ fontSize: '0.875rem', fontWeight: 500 }}
              >
                {crumb.label}
              </Typography>
            ) : (
              <Link
                key={index}
                component={RouterLink}
                to={crumb.to}
                underline="hover"
                color="inherit"
                sx={{ fontSize: '0.875rem' }}
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      {/* Título y acciones */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>

        {/* Botones de acción */}
        {actions && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Array.isArray(actions) ? (
              actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || 'contained'}
                  color={action.color || 'primary'}
                  onClick={action.onClick}
                  startIcon={action.icon ? <action.icon /> : undefined}
                  disabled={action.disabled}
                  size={action.size || 'medium'}
                >
                  {action.label}
                </Button>
              ))
            ) : (
              actions
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      to: PropTypes.string,
    })
  ),
  actions: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        onClick: PropTypes.func,
        icon: PropTypes.elementType,
        variant: PropTypes.string,
        color: PropTypes.string,
        disabled: PropTypes.bool,
        size: PropTypes.string,
      })
    ),
    PropTypes.node,
  ]),
};

export default PageHeader;
